import {DEFAULT_VALUES, VALUE_TYPES, setFilterValues, isNoAttributeFilter} from '~/assets/js/filters'
import {GRID_SETTINGS, MOBILE_BREAKPOINT, TOLERANCE_SET} from '~/assets/js/constants'
import useI18nExtended from './usei18nExtended'
const TYPE_NUMBER_VALUE_SEPARATOR = '-'
const TYPE_NUMBER_TOLERANCE_VALUE_SEPARATOR = '~'
const TYPE_STRING_VALUE_SEPARATOR = '$'

export const useCategoryFilters = (loadProductsFn: Function, categoryFilterRef: any) => {
  const route = useRoute()
  const router = useRouter()
  const i18n = useI18nExtended()

  // Variables
  const filters = ref([])
  const currentQuery = ref({})
  const tableOptions = ref({
    sort: [],
    pagination: {
      skip: DEFAULT_VALUES.SKIP,
      take: DEFAULT_VALUES.TAKE
    }
  })
  const isMobile = ref(false)
  const openFilterModal = ref(false)
  // eslint-disable-next-line no-undef
  const scrollTimeout: null | Timeout = ref(null)
  const currentQueryParams = ref({})
  const productTable = ref(null)
  const setOnce = ref(false)
  const hasActiveFilters = ref(false)
  const resetInProgress = ref(false)

  // Methods
  const setParamsInUrl = (l2category = null) => {
    let params = new URLSearchParams(currentQuery.value)
    setSortAndPaginationInUrl(params)
    setFiltersInUrl(params)
    let queryParams = Object.fromEntries(params)
    if (l2category) {
      queryParams.PrimaryL2Category = l2category
    }
    router.push({query: queryParams})
  }

  const setSortAndPaginationInUrl = (params: any) => {
    params.delete('order_by')
    params.delete('order')
    params.delete('skip')
    params.delete('take')

    if (tableOptions.value.sort.length > 0) {
      params.set('order_by', tableOptions.value.sort[0].field)
      params.set('order', tableOptions.value.sort[0].dir)
    }
    if (tableOptions.value.pagination.skip !== DEFAULT_VALUES.SKIP) {
      params.set('skip', tableOptions.value.pagination.skip)
    }
    if (tableOptions.value.pagination.take !== DEFAULT_VALUES.TAKE) {
      params.set('take', tableOptions.value.pagination.take)
    }
  }

  onMounted(() => {
    if (typeof window !== 'undefined') {
      onResize()
      window.addEventListener('resize', onResize, {passive: true})
    }

    if (route.query && !(route.name as string)?.startsWith('searchResults')) {
      setParamsFromUrl(route.query)
    }
  })

  onUnmounted(() => {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', onResize)
    }
  })

  const setFiltersInUrl = (params: any) => {
    if (filters.value && Array.isArray(filters.value)) {
      filters.value.forEach((filter) => {
        if (!filter.isActive) {
          params.delete(filter.shortName)
          return
        }

        let value
        const type = filter.type
        if (type === VALUE_TYPES.STRING) {
          value = filter.values.map(v => v.value).join(TYPE_STRING_VALUE_SEPARATOR)
        }
        else if (type === VALUE_TYPES.DROPDOWN || type === VALUE_TYPES.TEXT) {
          value = filter.values.value
        }
        else if (type === VALUE_TYPES.INTEGER || type === VALUE_TYPES.FLOAT || type === VALUE_TYPES.MULTI_STRING) {
          value = (filter.values.from ?? '') + TYPE_NUMBER_TOLERANCE_VALUE_SEPARATOR + (filter.values.tolerance ?? '')
        }
        else if (type === VALUE_TYPES.DATE) {
          value = (filter.values.start ?? '') + TYPE_NUMBER_VALUE_SEPARATOR + (filter.values.end ?? '')
        }
        else if (type === VALUE_TYPES.MULTI_DOUBLE) {
          value = filter.values.map(v => v.value).join(TYPE_STRING_VALUE_SEPARATOR)
        }
        else if (type === VALUE_TYPES.MULTI_INTEGER) {
          value = filter.values.map(v => v.value).join(TYPE_STRING_VALUE_SEPARATOR)
        }
        else if (type === VALUE_TYPES.BOOLEAN) {
          value = filter.values
        }
        if (value) {
          params.set(filter.shortName, value)
        }
      })
    }
  }

  const cleanParamsFromUrl = () => {
    let params = new URLSearchParams(route.query)
    const paramsCopy = new URLSearchParams(route.query)
    paramsCopy.forEach((value, key) => {
      if (!filters.value.find(f => f.shortName === key) && key !== 'query' && key !== 'newpdp' && key !== 'measurement') {
        params.delete(key)
      }
    })
    let queryParams = Object.fromEntries(params)
    router.push({query: queryParams})
  }
  // methods to set parameters from url
  const setParamsFromUrl = async (queryParams) => {
    setSortAndPaginationFromUrl(queryParams)
    setFiltersFromUrl(queryParams)
    hasActiveFilters.value = !!filters.value?.some(filter => filter.isActive)
    await nextTick()
    await loadProductsFn()
  }

  const setSortAndPaginationFromUrl = (queryParams: any) => {
    if (queryParams.order_by && queryParams.order) {
      tableOptions.value.sort = [{dir: queryParams.order, field: queryParams.order_by}]
    }
    if (queryParams.skip) {
      tableOptions.value.pagination.skip = parseInt(queryParams.skip)
    }
    if (queryParams.take) {
      tableOptions.value.pagination.take = parseInt(queryParams.take)
    }
  }

  const parseByType = (value: any, type: string) => {
    return type === VALUE_TYPES.INTEGER ? parseInt(value) : parseFloat(value)
  }

  const setFiltersFromUrl = (queryParams) => {
    if (filters.value && Array.isArray(filters.value)) {
      filters.value.forEach((filter) => {

        if (!isNoAttributeFilter(filter.shortName)) {
          filter.customTranslatePrefix = 'attributes.' + filter.shortName + '-values'
        }
        filter.isFilteredBy = false
        const paramValue = queryParams[filter.shortName]
        if (paramValue) {
          const type = filter.type
          let activeValues = null
          if (type === VALUE_TYPES.STRING || type === VALUE_TYPES.MULTI_STRING || type === VALUE_TYPES.MULTI_INTEGER) {
            const values = paramValue.split(TYPE_STRING_VALUE_SEPARATOR)
            const translationPrefix = filter.customTranslatePrefix ? filter.customTranslatePrefix : DEFAULT_VALUES.FILTER_TRANSLATE_PREFIX
            filter.values = values.map(value => (
              {
                label: filter.translateLabel ? this.$tv(value, translationPrefix) : i18n.tv(value, filter.customTranslatePrefix),
                value: value
              }))
            activeValues = filter.values.map(v => v.label)
          }
          else if (type === VALUE_TYPES.DROPDOWN || type === VALUE_TYPES.TEXT) {
            const translationPrefix = filter.customTranslatePrefix ? filter.customTranslatePrefix : DEFAULT_VALUES.FILTER_TRANSLATE_PREFIX
            filter.values = {
              label: filter.translateLabel ? this.$tv(paramValue, translationPrefix) : paramValue,
              value: paramValue
            }
            activeValues = paramValue
          }
          else if (type === VALUE_TYPES.INTEGER || type === VALUE_TYPES.FLOAT || type === VALUE_TYPES.MULTI_DOUBLE) {
            let from = null
            let tolerance = 0
            if (paramValue.includes(TYPE_NUMBER_VALUE_SEPARATOR)) {
              //handling legacy url params like "diameter=10-15"
              const splittedValue = paramValue.split(TYPE_NUMBER_VALUE_SEPARATOR)
              if (splittedValue.length > 1) {
                let value1 = splittedValue[0] ? parseByType(splittedValue[0], type) : 0
                let value2 = splittedValue[1] ? parseByType(splittedValue[1], type) : 0
                from = (value1 + value2) / 2
                tolerance = ((from - value1) / from) * 100
              } else if (splittedValue.length === 1) {
                from = splittedValue[0] ? parseByType(splittedValue[0], type) : 0
              }
            } else {
              //handling new url params like "diameter=10~10"
              const splittedValue = paramValue.split(TYPE_NUMBER_TOLERANCE_VALUE_SEPARATOR)
              if (splittedValue.length > 1) {
                from = splittedValue[0] ? parseByType(splittedValue[0], type) : 0
                tolerance = splittedValue[1] ? parseByType(splittedValue[1], type) : 0
              } else if (splittedValue.length === 1) {
                from = splittedValue[0] ? parseByType(splittedValue[0], type) : 0
              }
            }

            filter.values.from = from
            filter.values.tolerance = tolerance
            // use next bigger tolerance if chosenTolerance is not in dropdown
            let nextBiggerTolerance = null
            for (const tolerance of TOLERANCE_SET.ToleranceArray) {
              if (tolerance.value >= filter.values.tolerance) {
                nextBiggerTolerance = tolerance
                break
              }
            }

            if (filter.values.tolerance < nextBiggerTolerance?.value) {
              filter.values.tolerance = nextBiggerTolerance?.value
            }

            activeValues = filter.values
          }
          else if (type === VALUE_TYPES.DATE) {
            const splittedValue = paramValue.split(TYPE_NUMBER_VALUE_SEPARATOR)
            filter.values.start = splittedValue[0] ? splittedValue[0] : null
            filter.values.end = splittedValue[1] ? splittedValue[1] : null
            activeValues = filter.values
          } else if (type === VALUE_TYPES.BOOLEAN) {
            filter.values = paramValue === 'true'
            activeValues = filter.values
          }
          if (activeValues) {
            filter.isFilteredBy = true
            filter.activeValues = activeValues
            filter.isActive = true
          }
        }
        else {
          filter.values = setFilterValues(filter.type)
          filter.activeValues = null
          filter.isActive = false
        }
      })}
  }

  // sort/filter and pagination events
  const filter = (filtersLocal) => {
    filtersLocal.forEach(filter => {
      filter.isActive = isFilterActive(filter)
    })
    filters.value = filtersLocal
    tableOptions.value.pagination.skip = DEFAULT_VALUES.SKIP
    setParamsInUrl()
    scrollToProductTable()
  }

  const sortItems = (sort: any) => {
    tableOptions.value.sort = sort
    setParamsInUrl()
  }

  const changePage = (pagination: any) => {
    tableOptions.value.pagination = pagination
    setParamsInUrl()
  }

  const onResize = () => {
    isMobile.value = window.innerWidth < MOBILE_BREAKPOINT
  }
  const resetFilters = () => {
    resetInProgress.value = true
    nextTick(() => {
      categoryFilterRef?.resetFilters()
    })
    setTimeout(() => {
      hasActiveFilters.value = false
      resetInProgress.value = false
    }, 700)
  }

  const isFilterActive = (dataItem: any) => {
    if (!dataItem.isDisabled) {
      if (dataItem.type === VALUE_TYPES.STRING || dataItem.type === VALUE_TYPES.MULTI_STRING || dataItem.type === VALUE_TYPES.MULTI_INTEGER) {
        return dataItem?.values?.length > 0
      }
      else if (dataItem.type === VALUE_TYPES.DROPDOWN || dataItem.type === VALUE_TYPES.TEXT) {
        return dataItem.values && Object.keys(dataItem?.values).length > 0
      }
      else if (dataItem.type === VALUE_TYPES.INTEGER || dataItem.type === VALUE_TYPES.FLOAT || dataItem.type === VALUE_TYPES.MULTI_DOUBLE) {
        return !!(dataItem.values.from || dataItem.values.to)
      }
      else if (dataItem.type === VALUE_TYPES.DATE) {
        return !!(dataItem.values.start || dataItem.values.end)
      }
      else if (dataItem.type === VALUE_TYPES.BOOLEAN) {
        return dataItem.values
      }
    }
    return false
  }

  const scrollToProductTable = () => {
    if (scrollTimeout.value) {
      clearTimeout(scrollTimeout.value)
    }

    scrollTimeout.value = setTimeout(function() {
      if (!window) {
        return
      }

      const topPosition = (productTable.value as any)?.$el?.getBoundingClientRect()?.top ?? null
      const scrollPosition = document?.body?.getBoundingClientRect()?.top ?? null
      const windowHeight = window.innerHeight
      if (topPosition === null || scrollPosition === null || windowHeight === null) {
        return
      }

      const topScrollPosition = topPosition - scrollPosition + GRID_SETTINGS.APPLY_FILTER_THRESHOLD.TOP
      const windowScrollPosition = windowHeight - scrollPosition
      if (windowScrollPosition >= topScrollPosition) {
        return
      }

      window.scroll({top: topScrollPosition - windowHeight, behavior: 'smooth'})
    }, 250)
  }

  watch(() => route.query, async (queryParams) => {
    if (JSON.stringify(queryParams) !== JSON.stringify(currentQueryParams.value)) {
      currentQueryParams.value = {...queryParams}
      await setParamsFromUrl(queryParams)
    }
    hasActiveFilters.value = filters.value?.some(filter => filter.isActive)
  })

  watch(filters, async (newValue, oldValue) => {
    let queryParams = route.query
    if (newValue?.length !== oldValue?.length && !setOnce.value) {
      await setParamsFromUrl(queryParams)
      setOnce.value = true
    } else if (newValue?.length !== oldValue?.length) {
      cleanParamsFromUrl()
    }
  }, {deep: true})

  return {
    filters,
    tableOptions,
    isMobile,
    openFilterModal,
    currentQuery,
    parseByType,
    hasActiveFilters,
    cleanParamsFromUrl,
    resetInProgress,
    filter,
    sortItems,
    changePage,
    resetFilters,
    currentQueryParams,
    setOnce
  }

}
